import {
  type DocumentReleaseDetails,
  type WorkflowStepInput,
} from "~/api/types";
import { type UpdateDocumentReleaseData } from "~/api/v2/requests";
import { toISODateString, toISODateTimeString } from "~/helpers/date";
import {
  buildWorkflowDateChain,
  findOutOfOrderDates,
} from "~/helpers/workflow";
import { z } from "zod";
import i18n from "~/i18n";

export const workflowEditorFormSchema = z
  .object({
    reviewFrom: z.date().nullable(),
    reviewTo: z.date().nullable(),
    description: z.string(),
    steps: z.array(z.custom<WorkflowStepInput>()).refine(
      (steps) => steps.every((step) => step.name.trim().length > 0),
      i18n.t(($) => $.validations.allStepsMustHaveAName),
    ),
  })
  .refine(
    (data) => {
      if (!data.reviewFrom || !data.reviewTo) {
        return true;
      }

      const fromMs = new Date(data.reviewFrom).setHours(0, 0, 0, 0);
      const toMs = new Date(data.reviewTo).setHours(0, 0, 0, 0);

      return toMs >= fromMs;
    },
    {
      message: i18n.t(($) => $.validations.endDateAfter),
      path: ["reviewTo"],
    },
  )
  .refine(
    (data) =>
      findOutOfOrderDates(
        buildWorkflowDateChain(data.steps, data.reviewFrom, data.reviewTo),
      ).size === 0,
    {
      message: i18n.t(($) => $.validations.workflowDatesMustFollowPrevious),
      path: ["steps"],
    },
  );

export type WorkflowEditorFormValues = z.infer<typeof workflowEditorFormSchema>;

export function mapReleaseToFormValues(
  release: DocumentReleaseDetails | undefined,
): WorkflowEditorFormValues | undefined {
  if (!release) return undefined;

  const artifact = release.artifact;

  return {
    reviewFrom: artifact.reviewFrom ? new Date(artifact.reviewFrom) : null,
    reviewTo: artifact.reviewTo ? new Date(artifact.reviewTo) : null,
    description: artifact.description ?? "",
    steps: artifact.steps.map((step) => ({
      ...step,
      _key: `${step.id}`,
    })),
  };
}

export function mapFormValuesToUpdateReleaseData(
  values: WorkflowEditorFormValues,
  dirtyFields: Partial<Record<keyof WorkflowEditorFormValues, unknown>>,
): UpdateDocumentReleaseData {
  const data: UpdateDocumentReleaseData = {};

  if (dirtyFields.reviewFrom) {
    data.reviewFrom = toISODateString(values.reviewFrom);
  }
  if (dirtyFields.reviewTo) {
    data.reviewTo = toISODateString(values.reviewTo);
  }
  if (dirtyFields.description) {
    data.description = values.description;
  }
  if (dirtyFields.steps) {
    data.steps = values.steps.map((step) => ({
      id: step.id,
      name: step.name.trim(),
      description: step.description ?? null,
      type: step.type,
      dueAt: step.dueAt ? toISODateTimeString(step.dueAt) : null,
      users: step.users.map((user) => ({
        userId: user.userId,
        permission: user.permission,
      })),
    }));
  }

  return data;
}
